/**
 * The invoice line for time recorded from a note (attendance note, telephone
 * message, file note).
 *
 * Vanessa, 28 Oct 2026: the standard line ("Telephone attendance on ...")
 * shows first and the writer may edit it. "Suggest" asks Haiku for a line
 * (POST /time-entries/ai-narrative, template "attendance_note"), which is
 * told to name the subject in general terms only and never repeat the note:
 * the line goes on the client's invoice (BM-H4126). The server keeps what
 * is sent here and uses the standard line only when nothing is sent.
 *
 * `value === null` means "not edited": the field follows the standard line
 * as the type, channel or person change.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, RotateCcw } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export function invoiceLine(value, standardLine) {
  return value === null || value === undefined ? standardLine : value;
}

export default function InvoiceLineField({ value, onChange, standardLine, content, testId = "invoice-narrative" }) {
  const [suggesting, setSuggesting] = useState(false);
  const line = invoiceLine(value, standardLine);
  const edited = value !== null && value !== undefined && value !== standardLine;

  const suggest = async () => {
    if (!(content || "").trim()) {
      toast.info("Write the note first, then Suggest can draft a line from it.");
      return;
    }
    setSuggesting(true);
    try {
      const resp = await axios.post(
        `${API}/time-entries/ai-narrative`,
        { content: `${standardLine}\n\n${content}`, template: "attendance_note", subject_hint: standardLine },
        { withCredentials: true },
      );
      if (resp.data?.narrative) onChange(resp.data.narrative);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't get a suggestion. The line is unchanged."));
    }
    setSuggesting(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-xs text-slate-400 block">Invoice narrative</Label>
        <div className="flex items-center gap-3">
          {edited && (
            <button type="button" onClick={() => onChange(null)} title="Back to the standard line"
              className="text-[11px] text-slate-400 flex items-center gap-1 hover:underline"
              data-testid={`${testId}-reset`}>
              <RotateCcw className="w-3 h-3" /> Standard
            </button>
          )}
          <button type="button" onClick={suggest} disabled={suggesting}
            title="Haiku drafts a client-safe line from the note"
            className="text-[11px] text-indigo-300 flex items-center gap-1 hover:underline disabled:opacity-50"
            data-testid={`${testId}-suggest`}>
            {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Suggest
          </button>
        </div>
      </div>
      <Input
        value={line}
        onChange={(e) => onChange(e.target.value)}
        maxLength={300}
        className="bg-navy border-navy-border text-white text-sm"
        data-testid={`${testId}-input`}
      />
      <p className="text-[10px] text-slate-500 mt-1">
        Goes on the client's invoice. Never put the note's advice, figures or anything confidential here.
      </p>
    </div>
  );
}
